import { isTrustedAuthOrigin } from "@/lib/trusted-auth-origin";
import { shouldRedirectToDriver, shouldRedirectToYard } from "@/lib/role-access";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useUserRole } from "./hooks/useUserRole";
import { useAuthorization } from "./hooks/useAuthorization";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { SessionTimeoutWarning } from "./components/SessionTimeoutWarning";
import { toast } from "sonner";
import { EapiAuthProvider } from "./hooks/useEapiAuth";
import { TenantProvider } from "./hooks/useTenant";
import { OrderWindowMinutesSync } from "./hooks/useAppSetting";
import { DocumentTitleSync } from "./components/DocumentTitleSync";
import { ThemeProvider } from "next-themes";
import { useEffect } from "react";
import { shouldGuardHost, PROD_ORIGIN } from "./lib/host-allowlist";

// Pages
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import RecurringTemplateDetail from "./pages/RecurringTemplateDetail";
import Schedule from "./pages/Schedule";
import Maintenance from "./pages/Maintenance";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Locations from "./pages/Locations";
import Tractors from "./pages/Tractors";
import Trailers from "./pages/Trailers";
import Drivers from "./pages/Drivers";
import Admin from "./pages/Admin";
import DevPivotDialogs from "./pages/DevPivotDialogs";
import Atlas from "./pages/Atlas";
import Yard from "./pages/Yard";
import YardView from "./pages/YardView";
import DriverDashboard from "./pages/DriverDashboard";
import DriverAssignmentDetail from "./pages/DriverAssignmentDetail";
import DriverVerifyTrailer from "./pages/DriverVerifyTrailer";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import Documents from "./pages/Documents";
import AuthError from "./pages/AuthError";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Short stale window: a page that was just visited renders instantly
      // from cache, but anything older than 30s refetches on mount/focus so
      // users don't sit on outdated data.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Bounded retries with backoff — a single statement timeout shouldn't
      // strand the user on a stale render, and three serial retries on a
      // multi-second query made failures take far too long to surface.
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

function ProtectedRoute({ children, allowTerminalStaff = false }: { children: React.ReactNode; allowTerminalStaff?: boolean }) {
  const { user, loading } = useAuth();
  const { roles, isLoading: roleLoading } = useUserRole();
  const { isAuthorized, isLoading: authzLoading, isError: authzError, error: authzErrorObj, refetch: refetchAuthz } = useAuthorization();
  
  if (loading || roleLoading || authzLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Authorization RPC itself failed (network / transient JWT race / 5xx).
  // Do NOT redirect to /unauthorized — that screen is reserved for the
  // server definitively saying "no role, no allowed domain".
  if (authzError) {
    return <AuthError error={authzErrorObj} onRetry={() => refetchAuthz()} />;
  }

  // Server answered and user has no role + no allowed domain.
  if (!isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Driver-app users (driver, optionally + analyst) go to their dashboard
  if (shouldRedirectToDriver(roles)) {
    return <Navigate to="/my-assignments" replace />;
  }

  // Terminal staff without office access (no analyst/dispatcher/admin) go to yard
  if (shouldRedirectToYard(roles) && !allowTerminalStaff) {
    return <Navigate to="/yard" replace />;
  }
  
  return <>{children}</>;
}

function DriverRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { roles, isLoading: roleLoading } = useUserRole();
  const { isAuthorized, isLoading: authzLoading, isError: authzError, error: authzErrorObj, refetch: refetchAuthz } = useAuthorization();
  
  if (loading || roleLoading || authzLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (authzError) {
    return <AuthError error={authzErrorObj} onRetry={() => refetchAuthz()} />;
  }

  if (!isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Only admin, dispatcher, and driver can access this route
  const allowedRoles = ["admin", "dispatcher", "driver"];
  if (roles.length > 0 && !roles.some(r => allowedRoles.includes(r))) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { roles, isLoading: roleLoading } = useUserRole();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // Wait for role to load before redirecting
    if (roleLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      );
    }
    
    // Redirect based on role
    if (shouldRedirectToDriver(roles)) {
      return <Navigate to="/my-assignments" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function SessionTimeoutProvider({ children }: { children: React.ReactNode }) {
  const { showWarning, remainingTime, extendSession } = useSessionTimeout({
    onWarning: () => {
      toast.warning("Your session is about to expire", {
        description: "Click 'Continue Session' to stay logged in.",
      });
    },
    onTimeout: () => {
      toast.info("You have been logged out due to inactivity");
    },
  });

  return (
    <>
      {children}
      <SessionTimeoutWarning
        open={showWarning}
        remainingTime={remainingTime}
        onExtend={extendSession}
      />
    </>
  );
}

/**
 * When the iframe-breakout SSO tab finishes auth, it postMessages
 * AUTH_COMPLETE to its opener (this window). Reload so the preview pane
 * picks up the new Supabase session from storage.
 */
function AuthBreakoutListener() {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The breakout tab may live on a different Lovable origin
      // (sandbox vs preview vs published); only accept those origins.
      if (!isTrustedAuthOrigin(event.origin)) return;
      if (event.data?.type === "AUTH_COMPLETE") {
        window.location.reload();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return null;
}

/**
 * Redirect non-admin users off Lovable's preview and share hosts to the same
 * path on the published app. Only those hosts: localhost, the Lovable sandbox
 * dev host and every deploy of this repo (tms.aurora.tech, its dev and staging
 * subdomains, and the pr-<N> previews) are left alone, and admins and
 * unauthenticated visitors are unaffected either way, so QA and the login flow
 * keep working.
 *
 * Lovable previews are worth the redirect because they read the committed
 * `.env`, which points at Lovable's production Supabase project, so a shared
 * preview link is unpublished UI operating on real data. Client-side only, and
 * not a security boundary; RLS still enforces data access.
 */
function HostGuard({ children }: { children: React.ReactNode }) {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const guarded = shouldGuardHost(hostname);

  const { user, loading } = useAuth();
  const { roles, isLoading: roleLoading } = useUserRole();

  if (!guarded) {
    return <>{children}</>;
  }

  // Wait for auth + role resolution before deciding — avoids flashing
  // preview UI to a non-admin while the role query is in flight.
  if (loading || (user && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Anonymous visitors can stay (they need /auth to sign in).
  if (!user) {
    return <>{children}</>;
  }

  // Admins keep full preview access for QA.
  if (roles.includes("admin")) {
    return <>{children}</>;
  }

  // Explicit QA opt-in: append ?qa=1 to any preview URL to stay on the
  // unpublished build for the rest of the browser session. Client-side
  // convenience only — RLS still governs what the user can see or change.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("qa") === "1") {
      window.sessionStorage.setItem("atms_preview_qa", "1");
    }
    if (window.sessionStorage.getItem("atms_preview_qa") === "1") {
      return <>{children}</>;
    }
  } catch {
    // sessionStorage unavailable (private mode) — fall through to redirect.
  }



  // Everyone else: bounce to the equivalent path on prod.
  const { pathname, search, hash } = window.location;
  window.location.replace(`${PROD_ORIGIN}${pathname}${search}${hash}`);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Redirecting…</div>
    </div>
  );
}

function AppRoutes() {
  return (
    <SessionTimeoutProvider>
      <AuthBreakoutListener />
      <OrderWindowMinutesSync />
      <DocumentTitleSync />
      <Routes>
        <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/my-assignments" element={<DriverRoute><DriverDashboard /></DriverRoute>} />
        <Route path="/my-assignments/:id" element={<DriverRoute><DriverAssignmentDetail /></DriverRoute>} />
        <Route
          path="/my-assignments/:id/verify-trailer"
          element={<DriverRoute><DriverVerifyTrailer /></DriverRoute>}
        />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
        <Route path="/templates/:id" element={<ProtectedRoute><RecurringTemplateDetail /></ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
        <Route path="/asset-schedule" element={<Navigate to="/schedule" replace />} />
        <Route path="/driver-schedule" element={<Navigate to="/schedule" replace />} />
        <Route path="/maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
        <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
        <Route path="/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
        <Route path="/tractors" element={<ProtectedRoute><Tractors /></ProtectedRoute>} />
        <Route path="/trailers" element={<ProtectedRoute><Trailers /></ProtectedRoute>} />
        <Route path="/drivers" element={<ProtectedRoute><Drivers /></ProtectedRoute>} />
        <Route path="/atlas" element={<ProtectedRoute><Atlas /></ProtectedRoute>} />
        <Route path="/yard" element={<ProtectedRoute allowTerminalStaff><Yard /></ProtectedRoute>} />
        <Route path="/yard-view" element={<ProtectedRoute><YardView /></ProtectedRoute>} />
        
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/dev/pivot-dialogs" element={<ProtectedRoute><DevPivotDialogs /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SessionTimeoutProvider>
  );
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem storageKey="atms-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TenantProvider>
          <EapiAuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <HostGuard>
                  <AppRoutes />
                </HostGuard>
              </BrowserRouter>
            </TooltipProvider>
          </EapiAuthProvider>
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
